import { CoveoProductContext } from "@DSG/Components/Coveo/CoveoContext/CoveoProductContext";
import CoveoProductAddToCartButton from "@DSG/Components/Coveo/ProductCardView/CoveoProductAddToCartButton";
import CoveoProductAddToListLink from "@DSG/Components/Coveo/ProductCardView/CoveoProductAddToListLink";
import CoveoProductPrice from "@DSG/Components/Coveo/ProductCardView/CoveoProductPrice";
import CoveoProductQuantityOrdered from "@DSG/Components/Coveo/ProductCardView/CoveoProductQuantityOrdered";
import CoveoProductTextBadges from "@DSG/Components/Coveo/ProductListView/CoveoProductTextBadges";
import CoveoViewSignInPricingButton from "@DSG/Components/Coveo/ProductListView/CoveoViewSignInPricingButton";
import colorPallete, { bodyFonts, buttonFonts, labelFonts, themeFontWeights } from "@DSG/MobiusOverrides/styles/variables";
import { getSession } from "@DSG/Overrides/Store/Context/ContextSelectors";
import { CoveoProductModel } from "@DSG/Type/CoveoModels";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import sendProductRecommendationTracking from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/SendProductRecommendationTracking";
import translate from "@insite/client-framework/Translate";
import {
  ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import {
  ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Img from "@insite/mobius/Img";
import Link from "@insite/mobius/Link";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.productId),
    isAuthenticated: getSession(state).isAuthenticated,
});

const mapDispatchToProps = {
    updateProductInfo,
    sendProductRecommendationTracking,
};

interface OwnProps {
    carouselId: string;
    carouselType: string;
    product: CoveoProductModel;
    recommendationId?: number;
    showImage: boolean;
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showPrice: boolean;
    showAddToCart: boolean;
    showAddToList: boolean;
    showImageBadges?: boolean;
    maximumNumberOfImageBadges?: number;
    showTextBadges?: boolean;
    maximumNumberOfTextBadges?: number;
    extendedStyles?: CoveoProductCarouselProductStyles;
    showViewProduct: boolean;
    showQtyBox:boolean;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface CoveoProductCarouselProductStyles {
    productCardWrapper?: InjectableCss;
    productItemWrapper: InjectableCss;
    productImageWrapper: InjectableCss;
    productDescriptionWrapper: InjectableCss;
    productImageStyles?: ProductImageStyles;
    productBrandWrapper?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productPriceWrapper?: InjectableCss;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    addToCartContainer?: GridContainerProps;
    qtyGridItem?: GridItemProps;
    productQuantityOrderedStyles?: TextFieldProps;
    addToCartButtonGridItem?: GridItemProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
    viewProductButton?: InjectableCss;
    productPartNumberWrapper?: InjectableCss;
    buttonWrapper?: InjectableCss;
    brandWrapper?: InjectableCss;
    brandMainwrapper?: InjectableCss;
}

export const coveoProductCarouselProductStyles: CoveoProductCarouselProductStyles = {
    productPartNumberWrapper: {
        css: css`
            margin-bottom: 8px;
            margin-top: 0;
            * {
                font: ${labelFonts.label1Regular.fontWeight} ${labelFonts.label1Regular.fontSize}/${labelFonts.label1Regular.lineHeight} ${labelFonts.label1Regular.fontFamily};
            }
        `
    },
    
    productItemWrapper: {
        css: css`
            position: relative;
        `,
    },
    productImageWrapper: {
        css: css`
            padding-top: 0;
            [data-test-selector="coveoProductImage"] {
                display: flex;
                justify-content: center;
                > span {
                    display: flex;
                }
            }
        `,
    },
    brandMainwrapper: {
        css: css`
            display: flex;
            min-height: 22px;
        `
    },
    brandWrapper: {
        css: css`
            line-height: ${labelFonts.label1Regular.lineHeight};
            display: inline;
            [class*="StyledClickable"] {
                display: inline;
                &,
                > span[class*="StyledTypography"] {
                    color: ${colorPallete.grey600};
                }
            }
            [data-test-selector="subBrandSeparator"] {
                color: ${colorPallete.grey700};
                display: inline;
                position: relative;
                top: 1px;
                font-weight: ${themeFontWeights.regular};
            }
        `
    },
    productDescriptionWrapper: {
        css: css`
            [data-test-selector="addToListLink"] {
                margin-bottom: 16px;
            }
        `,
    },
    productBrandWrapper: {
        css: css`
            margin: 16px 0 4px 0;
            display: flex;
            [data-test-selector="brandLink"] span {
                display: -webkit-box;
                -webkit-box-orient: vertical;
                -webkit-line-clamp: 1;
                align-self: stretch;
                overflow: hidden;
            }
        `,
    },
    productDescriptionStyles: {
        productDetailLink: {
            css: css`
                margin-bottom: 6px;
                display: -webkit-box;
                -webkit-line-clamp: 3;
                -webkit-box-orient: vertical;
                overflow: hidden;
                color: ${colorPallete.black90};
                font-weight: ${themeFontWeights.semiBold};
                font-size: ${bodyFonts.body2SemiBoldLg.fontSize};
                line-height: ${bodyFonts.body2SemiBoldLg.lineHeight};
                @media only screen and (max-width: 768px) {
                    font-size: ${labelFonts.label1SemiBold.fontSize};
                    line-height: ${labelFonts.label1SemiBold.lineHeight};       
                    margin-bottom: 0;
                }
            `,
        },
    },
    productPartNumbersStyles: {
        container: {
            css: css`
                flex-grow: 0;
                margin-bottom: 8px;
            `,
        },
        erpNumberLabelText: { size: 13 },
        erpNumberValueText: { size: 13 },
    },
    productPriceWrapper: {
        css: css`
            &:not(:empty) {
                margin-bottom: 8px;
            }  
            [data-test-selector="productPriceSavingsMessage"] {
                margin-top: 8px;
            }
            .priceWrapper {
                margin-bottom: 0;
            }
            .failedToLoad {
                border: none;
                padding: 0;
                border-radius: 0;
                [class*="IconWrapper"] {
                    display: none;  
                }
                .priceLoadFailed {
                    margin: 0;
                }
            }
        `,
    },
    productUnitOfMeasureSelectStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 6px;
            `,
        },
    },
    addToCartContainer: {
        gap: 10,
        css: css`
            flex-grow: 0;
            margin-bottom: 16px;
            margin-top: auto;
        `,
    },
    qtyGridItem: { width: 12 },
    productQuantityOrderedStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 0;
            `,
        },
    },
    addToCartButtonGridItem: { width: 9 },
    productAddToCartButtonStyles: {
        css: css`
            width: 100%;
        `,
    },
    productAddToListLinkStyles: {
        link: {
            typographyProps: { size: 13 },
            css: css`
                margin-bottom: 12px;
                margin-bottom: 0'
            `,
        },
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
            padding-top: 36px;
            .tooltipBadgeWrapper {
                .tooltipBadgeText {
                    margin: 0;
                }
            }
            [class*="TextBadge-"] {
                padding: 8px;
                border-radius: 4px;
                font-style: italic;
                font-size: 10px;
                line-height: 10px;
            }
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
            white-space: nowrap;
            overflow: hidden;
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: "bold",
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
        `,
    },
    viewProductButton: {
        css: css`
            justify-content: center;
            min-height: 38px;
        `
    },
    buttonWrapper: {
        css: css`
            margin-top: auto;
            flex-direction: column;
            gap: 8px;
            padding-top: 8px;
            display: flex;
            &:empty {
                display: none;
            }
            .number-input {
                margin: 0;
            }
            [data-test-selector="AddToList"] {
                display: block;
                padding: 0 32px;
                min-height: 38px;
                border-radius: 30px;
                width: 100%;
                border: 2px solid ${colorPallete.primaryRed500};
                > span {
                    font: ${buttonFonts.buttonSm.fontWeight} ${buttonFonts.buttonSm.fontSize}/${buttonFonts.buttonSm.lineHeight} ${buttonFonts.buttonSm.fontFamily};
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                min-height: 48px;
                                > span {
                                    font-size: ${buttonFonts.buttonLg.fontSize};
                                    line-height: ${buttonFonts.buttonLg.lineHeight};
                                }
                            `,
                        ],
                        "min",
                    )}
                svg {
                    display: none;
                }
                span {
                    display: block;
                    text-align: center;
                    color: ${colorPallete.primaryRed500}
                }
                &:hover {
                    background: ${colorPallete.primaryMaroon500};
                    border: 2px solid ${colorPallete.primaryMaroon500};
                    span {
                        color: ${colorPallete.white};
                    }
                }

                &:active {
                    background: ${colorPallete.grey800};
                    border: 2px solid ${colorPallete.grey800};
                }

                &:hover,
                &:active {
                    color: ${colorPallete.white};
                    span {
                        color: ${colorPallete.white};
                    }
                }
                &:disabled {
                    border: 2px solid ${colorPallete.grey400};
                    background: ${colorPallete.white};
                    span {
                        color: ${colorPallete.grey400};
                    }
                }
            }
        `
    },
};

const CoveoProductCarouselProduct = ({
    carouselId,
    carouselType,
    product,
    showImage,
    showBrand,
    showTitle,
    showPartNumbers,
    showPrice,
    showAddToCart,
    showAddToList,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    updateProductInfo,
    extendedStyles,
    productInfo,
    recommendationId,
    sendProductRecommendationTracking,
    showViewProduct,
    showQtyBox,
    isAuthenticated
}: Props) => {
    const [styles] = useState(() => mergeToNew(coveoProductCarouselProductStyles, extendedStyles));

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.productId, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.productId, unitOfMeasure });
    };

    // if (!product || !productInfo) {
    //     return null;
    // }

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };
    const coveoProductContext = useContext(CoveoProductContext);

    // const trackProductRecommendation = (isNewPageWasOpen: boolean) => {
    //     if (recommendationId) {
    //         sendProductRecommendationTracking({ recommendationId, isNewPageWasOpen });
    //     }
    // };

    // const { showOverlayBadge, overlayBadge, overlayBadgeWrapperStyles } = useOverlayBadge({
    //     productContext,
    //     showTextBadge: showTextBadges,
    //     showImageBadge: showImageBadges,
    //     styles,
    // });

   // const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

   // const productDetailPath = productInfo ? productInfo.productDetailPath : product.urlSegment;

   const handleLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
      product.interactiveProduct?.select();
    };
   
    return (
        <>
            <StyledWrapper {...styles.productItemWrapper} className="product-item-wrapper">
                {
                    showImage && !showTextBadges && (
                        <StyledWrapper className="product-image-wrapper" {...styles.productImageWrapper}>
                            <Link
                                href={product.urlSegment}
                                data-test-selector="coveoProductImage"    
                            >
                                <Img src={product.smallImagePath} altText={product.imageAltText}/>
                            </Link>
                        </StyledWrapper>
                    )
                }
                {showImage && showTextBadges && (
                    <StyledWrapper {...styles.badgeAreaWrapper} data-test-selector="productCarouselBadge" className="product-image-wrapper" {...styles.productImageWrapper}>
                        <StyledWrapper >
                            {product?.badges && (
                                <CoveoProductTextBadges badgeList={product?.badges} maxBadges={maximumNumberOfTextBadges ?? 2} />
                            )}
                        </StyledWrapper> 
                        <Link
                            href={product.urlSegment}
                            data-test-selector="coveoProductImage"    
                            onClick={handleLinkClick} 
                        >
                            <Img src={product.smallImagePath} altText={product.imageAltText}/>
                        </Link>
                      
                        {/* <StyledWrapper {...overlayBadgeWrapperStyles} className="overlayBadgeWrapper">
                            <Badge
                                type={overlayBadge.badgeType}
                                value={
                                    overlayBadge.badgeType === "Image"
                                        ? overlayBadge.largeImageBadgePath
                                        : overlayBadge.displayText
                                }
                                badgeTextColor={overlayBadge.textColorHexCode}
                                badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                                imageAltText={
                                    overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name
                                }
                                textBadgeCss={styles.badgeText}
                                imageBadgeProps={styles.badgeImageProps}
                            />
                        </StyledWrapper> */}
                    </StyledWrapper> 
                )}
            </StyledWrapper>
            <StyledWrapper className="product-description-wrapper" {...styles.productDescriptionWrapper}>
                <StyledWrapper {...styles.productBrandWrapper} className="productBrandNameWrapper">
                    {showBrand && product.brandName && (
                       <StyledWrapper {...styles.brandMainwrapper} className="brandsOuterWrapper">
                            <StyledWrapper {...styles.brandWrapper} className="brandAndSubBrandWrapper">
                                {product.subBrandName && (
                                    <>
                                    <Link href={`${product.brandUrlSegment}#f-ec_brand_name_sub=${product.subBrandName}`} data-test-selector="brandLink">
                                    {product.subBrandName}
                                    </Link>
                                    <Typography data-test-selector="subBrandSeparator">
                                        {translate(" by ")}
                                    </Typography>
                                    </>
                                )}
                                 
                                <Link href={product.brandUrlSegment} data-test-selector="brandLink">
                                    {product.brandName}
                                </Link>    
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                </StyledWrapper>
                {(showTitle && product.title) && (
                    <Link 
                        href={product.urlSegment} 
                        data-test-selector="productDescriptionLink"
                        onClick={handleLinkClick}
                    >
                        {product.title}
                    </Link>
                )}
                {showPartNumbers && (
                    <StyledWrapper {...styles.productPartNumberWrapper}>
                        {product.manufacturerItem && (
                            <StyledWrapper data-test-selector="mfgPartNumber">
                                <Typography >{translate("MFG Part#")}</Typography>                                                    
                                <Typography >{product.manufacturerItem}</Typography>                                                    
                            </StyledWrapper>
                        )}
                        {product.productNumber && (
                            <StyledWrapper data-test-selector="dsgNumber">
                                <Typography >{translate("DSG#")}</Typography>                                                    
                                <Typography >{product.productNumber}</Typography>                                                    
                            </StyledWrapper>
                        )}
                        {product.upcCode && (
                            <StyledWrapper  data-test-selector="upcNumber">
                                <Typography >{translate("UPC#")}</Typography>                                                    
                                <Typography >{product.upcCode}</Typography>                                                    
                            </StyledWrapper>
                        )}
                        {product.customerProductNumber && (
                            <StyledWrapper data-test-selector="myPartNumber">
                                <Typography >{translate("My Part#")}</Typography>                                                    
                                <Typography >{product.customerProductNumber}</Typography>                                                    
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                )}
                
                {showPrice && (
                    <StyledWrapper {...styles.productPriceWrapper} className={showPrice ? 'productPriceWrapper' : ''}>
                        {coveoProductContext && (
                            <CoveoProductPrice coveoProduct={coveoProductContext}
                                showLabel={false}
                                showSavings={true}
                                showSavingsAmount={false}
                                showSavingsPercent={false}
                                extendedStyles={undefined}
                            />
                        )}
                    </StyledWrapper>
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.buttonWrapper}>
                {isAuthenticated && (
                    <>
                        {carouselType !== "topSellers" && (
                            showQtyBox && (
                                <CoveoProductQuantityOrdered  extendedStyles={styles.productQuantityOrderedStyles}/>
                            )
                        )}
                        {showAddToList && (
                            <CoveoProductAddToListLink
                                data-test-selector="AddToList"
                            />
                        )}
                        {showAddToCart && (
                            <CoveoProductAddToCartButton
                                    data-test-selector="addToCartBtn"
                                extendedStyles={styles.productAddToCartButtonStyles}
                            />
                        )}
                    </>
                )}
                {showPrice && showAddToCart && !isAuthenticated && (
                    <CoveoViewSignInPricingButton />
                )}
                {showViewProduct && (
                    <Clickable
                        href={product.urlSegment}
                        {...styles.viewProductButton}
                        data-test-selector={`viewProductBtn_${product.productNumber}`}
                        className="btn btn-secondary"
                        onClick={handleLinkClick}
                    >
                        {translate("View product")}
                    </Clickable>
                )}
            </StyledWrapper>
     
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(CoveoProductCarouselProduct);
